import { API_URL } from '../constants';
import { fetchWithAuth } from '@/helpers/auth';
import { getCookie } from './cookiesControls';

type ApiVersion = 'v1' | 'v2';
export type ApiRequestInit = RequestInit & { apiVersion?: ApiVersion };

function getUrlWithBase(url: string, version: 'v1' | 'v2' = 'v1') {
  return `${API_URL}/${version}${url.startsWith('/') ? url : `/${url}`}`;
}

function prepareFetchArgs(url: string, params?: ApiRequestInit) {
  const apiVersion = params?.apiVersion ?? 'v1';
  const { apiVersion: ignoredVersion, ...fetchParams } = params || {};

  return {
    urlWithBase: getUrlWithBase(url, apiVersion),
    fetchParams,
  };
}

const defaultHeaders = async () => {
  const locale = getCookie('NEXT_LOCALE') || 'en';

  return {
    Accept: 'application/json',
    'Content-Type': 'application/json',
    'Accept-Language': locale,
  };
};

const writeHeaders = async () => {
  const cookieReferer = getCookie('referer') || '';
  const headers = await defaultHeaders();

  return {
    ...headers,
    'Original-Referer': cookieReferer,
  };
};

const apiClient = {
  async get<ResponseType>(url: string, params?: RequestInit) {
    const { urlWithBase, fetchParams } = prepareFetchArgs(url, params);
    const headers = await defaultHeaders();

    const response = await fetch(urlWithBase, {
      method: 'GET',
      headers,
      ...fetchParams,
    });

    if (response.status === 404) throw new Error('404 Not Found');
    return (await response.json()) as ResponseType;
  },

  async getResponse<ResponseType>(url: string, params?: ApiRequestInit) {
    const { urlWithBase, fetchParams } = prepareFetchArgs(url, params);
    const headers = await defaultHeaders();

    const response = await fetch(urlWithBase, {
      method: 'GET',
      headers,
      ...fetchParams,
    });
    const isJson = response.headers.get('content-type')?.startsWith('application/json');
    return {
      status: response.status,
      response: (isJson ? await response.json() : (response as unknown)) as ResponseType,
    };
  },

  async post<ResponseType>(url: string, params?: ApiRequestInit) {
    const { urlWithBase, fetchParams } = prepareFetchArgs(url, params);
    const headers = await writeHeaders();

    const response = await fetch(urlWithBase, {
      method: 'POST',
      headers,
      referrerPolicy: 'no-referrer-when-downgrade',
      ...fetchParams,
    });
    return (await response.json()) as ResponseType;
  },

  async postResponse<ResponseType>(url: string, params?: ApiRequestInit) {
    const { urlWithBase, fetchParams } = prepareFetchArgs(url, params);
    const headers = await writeHeaders();

    const response = await fetch(urlWithBase, {
      method: 'POST',
      headers,
      referrerPolicy: 'no-referrer-when-downgrade',
      ...fetchParams,
    });
    return { status: response.status, response: (await response.json()) as ResponseType };
  },

  async getBlobWithAuth(url: string, params?: ApiRequestInit): Promise<Blob> {
    const { urlWithBase, fetchParams } = prepareFetchArgs(url, params);
    const headers = await defaultHeaders();

    const res = await fetchWithAuth(urlWithBase, {
      method: 'GET',
      headers,
      ...fetchParams,
    });

    if (!res.ok) {
      throw new Error(`Error fetching file: ${res.status}`);
    }

    return res.blob();
  },

  async getWithAuth<ResponseType>(url: string, params?: ApiRequestInit) {
    const { urlWithBase, fetchParams } = prepareFetchArgs(url, params);
    const headers = await defaultHeaders();

    const res = await fetchWithAuth(urlWithBase, {
      method: 'GET',
      headers,
      ...fetchParams,
    });
    if (res.status === 404) throw new Error('404 Not Found');
    return (await res.json()) as ResponseType;
  },

  async getResponseWithAuth<ResponseType>(url: string, params?: ApiRequestInit) {
    const { urlWithBase, fetchParams } = prepareFetchArgs(url, params);
    const headers = await defaultHeaders();

    const res = await fetchWithAuth(urlWithBase, {
      method: 'GET',
      headers,
      ...fetchParams,
    });
    const isJson = res.headers.get('content-type')?.startsWith('application/json');
    return {
      status: res.status,
      response: (isJson ? await res.json() : (res as unknown)) as ResponseType,
    };
  },

  async postWithAuth<ResponseType>(url: string, params?: ApiRequestInit) {
    const { urlWithBase, fetchParams } = prepareFetchArgs(url, params);
    const headers = await writeHeaders();

    const res = await fetchWithAuth(urlWithBase, {
      method: 'POST',
      headers,
      referrerPolicy: 'no-referrer-when-downgrade',
      ...fetchParams,
    });
    return (await res.json()) as ResponseType;
  },

  async postResponseWithAuth<ResponseType>(url: string, params?: ApiRequestInit) {
    const { urlWithBase, fetchParams } = prepareFetchArgs(url, params);
    const headers = await writeHeaders();

    const res = await fetchWithAuth(urlWithBase, {
      method: 'POST',
      headers,
      referrerPolicy: 'no-referrer-when-downgrade',
      ...fetchParams,
    });
    return { status: res.status, response: (await res.json()) as ResponseType };
  },

  async put<ResponseType>(url: string, params?: ApiRequestInit) {
    const { urlWithBase, fetchParams } = prepareFetchArgs(url, params);
    const headers = await writeHeaders();

    const response = await fetch(urlWithBase, {
      method: 'PUT',
      headers,
      referrerPolicy: 'no-referrer-when-downgrade',
      ...fetchParams,
    });
    return (await response.json()) as ResponseType;
  },

  async putResponse<ResponseType>(url: string, params?: ApiRequestInit) {
    const { urlWithBase, fetchParams } = prepareFetchArgs(url, params);
    const headers = await writeHeaders();

    const response = await fetch(urlWithBase, {
      method: 'PUT',
      headers,
      referrerPolicy: 'no-referrer-when-downgrade',
      ...fetchParams,
    });
    return { status: response.status, response: (await response.json()) as ResponseType };
  },

  async putWithAuth<ResponseType>(url: string, params?: ApiRequestInit) {
    const { urlWithBase, fetchParams } = prepareFetchArgs(url, params);
    const headers = await writeHeaders();

    const res = await fetchWithAuth(urlWithBase, {
      method: 'PUT',
      headers,
      referrerPolicy: 'no-referrer-when-downgrade',
      ...fetchParams,
    });
    return (await res.json()) as ResponseType;
  },

  async putResponseWithAuth<ResponseType>(url: string, params?: ApiRequestInit) {
    const { urlWithBase, fetchParams } = prepareFetchArgs(url, params);
    const headers = await writeHeaders();

    const res = await fetchWithAuth(urlWithBase, {
      method: 'PUT',
      headers,
      referrerPolicy: 'no-referrer-when-downgrade',
      ...fetchParams,
    });
    return { status: res.status, response: (await res.json()) as ResponseType };
  },

  async delete<ResponseType>(url: string, params?: ApiRequestInit) {
    const { urlWithBase, fetchParams } = prepareFetchArgs(url, params);
    const headers = await writeHeaders();

    const response = await fetch(urlWithBase, {
      method: 'DELETE',
      headers,
      referrerPolicy: 'no-referrer-when-downgrade',
      ...fetchParams,
    });
    return (await response.json()) as ResponseType;
  },

  async deleteResponse<ResponseType>(url: string, params?: ApiRequestInit) {
    const { urlWithBase, fetchParams } = prepareFetchArgs(url, params);
    const headers = await writeHeaders();

    const response = await fetch(urlWithBase, {
      method: 'DELETE',
      headers,
      referrerPolicy: 'no-referrer-when-downgrade',
      ...fetchParams,
    });
    return { status: response.status, response: (await response.json()) as ResponseType };
  },

  async deleteWithAuth<ResponseType>(url: string, params?: ApiRequestInit) {
    const { urlWithBase, fetchParams } = prepareFetchArgs(url, params);
    const headers = await writeHeaders();

    const res = await fetchWithAuth(urlWithBase, {
      method: 'DELETE',
      headers,
      referrerPolicy: 'no-referrer-when-downgrade',
      ...fetchParams,
    });
    return (await res.json()) as ResponseType;
  },

  async deleteResponseWithAuth<ResponseType>(url: string, params?: ApiRequestInit) {
    const { urlWithBase, fetchParams } = prepareFetchArgs(url, params);
    const headers = await writeHeaders();

    const res = await fetchWithAuth(urlWithBase, {
      method: 'DELETE',
      headers,
      referrerPolicy: 'no-referrer-when-downgrade',
      ...fetchParams,
    });
    return { status: res.status, response: (await res.json()) as ResponseType };
  },
};

export default apiClient;
